import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Link,
  Divider,
  IconButton,
  alpha,
} from "@mui/material";

import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import MoneyOffOutlinedIcon from "@mui/icons-material/MoneyOffOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import BuySellOrderTable from "./BuySellOrderTable";
import P2PNavbar from "./P2PNavbar";
import { useSelector } from "react-redux";
import { ContainedButton, OutlinedButton } from "../../Common/Buttons";

const P2pDashboard = () => {
  const theme = localStorage.getItem("theme");
  const { btncolor } = useSelector((state) => state);
  const [noticeModal, setNoticeModal] = useState(false);
  const [firstCheckBox, setFirstCheckBox] = useState(false);
  const [secondCheckBox, setSecondCheckBox] = useState(false);
  const [thirdCheckBox, setThirdCheckBox] = useState(false);

  const [p2pTermsLink, setP2pTermsLink] = useState(
    window.location.origin + "/legal/p2p-user.html"
  );

  // Open modal on mount unless third checkbox was previously checked
  useEffect(() => {
    const skipNotice = localStorage.getItem("skipNoticeModal");
    if (skipNotice !== "true") {
      setNoticeModal(true);
    }
  }, []);

  // Handle checkbox updates
  const handleFirstCheckbox = (e) => setFirstCheckBox(e.target.checked);
  const handleSecondCheckbox = (e) => setSecondCheckBox(e.target.checked);
  const handleThirdCheckbox = (e) => {
    const checked = e.target.checked;
    setThirdCheckBox(checked);
  };

  // Button should be disabled unless both first and second are checked
  const isConfirmDisabled = !(firstCheckBox && secondCheckBox);

  const handleConfirm = () => {
    setNoticeModal(false);
    localStorage.setItem("skipNoticeModal", thirdCheckBox.toString());
  };

  const features = [
    {
      icon: <ShieldOutlinedIcon sx={{ fontSize: 22 }} />,
      title: "Flexible Payment Methods",
      desc: "P2P provides a safe platform to conduct crypto trades in 500+ payment methods and 100+ fiat currencies.",
    },
    {
      icon: <PublicOutlinedIcon sx={{ fontSize: 22 }} />,
      title: "Access a Global Marketplace",
      desc: "Buy, sell and trade crypto directly with other users with your preferred prices and payment methods in an open global crypto marketplace.",
    },
    {
      icon: <MoneyOffOutlinedIcon sx={{ fontSize: 22 }} />,
      title: "Zero Trading Fees",
      desc: "Trade P2P with zero fees and your preferred payment method.",
    },
  ];

  return (
    <Box sx={pageWrapper}>
      {/* ── Hero Banner ── */}
      <Box
        sx={{
          ...heroBanner,
          background: `linear-gradient(135deg, ${alpha(
            btncolor || "#1976d2",
            0.95
          )} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
        }}
      >
        <Box sx={heroContent}>
          <Box sx={heroIconBadge}>
            <SwapHorizOutlinedIcon sx={{ color: "#fff", fontSize: 30 }} />
          </Box>
          <Box>
            <Typography sx={heroTitle}>P2P Exchange</Typography>
            <Typography sx={heroSubtitle}>
              Buy and sell crypto directly with other users at your preferred
              prices and payment methods
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Main Card: Navbar + Features ── */}
      <Box sx={mainCard}>
        <P2PNavbar />

        {/* Feature Cards */}
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={featureGrid}>
            {features.map((f, i) => (
              <Box key={i} sx={featureCard}>
                <Box
                  sx={{
                    ...featureIconWrap,
                    backgroundColor: alpha(btncolor || "#1976d2", 0.1),
                    color: btncolor,
                  }}
                >
                  {f.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={featureTitle}>{f.title}</Typography>
                  <Typography sx={featureDesc}>{f.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 3 }}>
            <a
              href={`${window.location.origin}/p2p.html`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <OutlinedButton
                variant="outlined"
                endIcon={<ArrowForwardIcon />}
                sx={howItWorksBtn}
              >
                How P2P Works
              </OutlinedButton>
            </a>
          </Box>
        </Box>
      </Box>

      {/* ── Buy / Sell Order Table ── */}
      <BuySellOrderTable />

      {/* ── Notice Dialog ── */}
      <Dialog
        open={noticeModal}
        onClose={() => setNoticeModal(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaper }}
      >
        <DialogTitle sx={dialogTitleStyle}>
          Notice
          <IconButton
            onClick={() => setNoticeModal(false)}
            size="small"
            sx={dialogCloseBtn}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />

        <DialogContent sx={dialogContentStyle}>
          {[
            "I understand that I will not transfer my crypto assets before confirming that the payment has reached me.",
            "I understand that I shall not believe anyone attempting to convince me to complete the transaction before I receive the payment.",
            "I understand that a P2P transaction is considered completed, when the seller receives the payment, confirms the order and transfers the assets to the buyer.",
            "I understand that the platform shall not take any responsibility for transactions made outside the platform.",
          ].map((text, i) => (
            <Typography key={i} sx={noticeText}>
              {text}
            </Typography>
          ))}

          <Box sx={{ mt: 2.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={firstCheckBox}
                  onChange={handleFirstCheckbox}
                  sx={{
                    color: "rgba(125,125,125,0.5)",
                    "&.Mui-checked": { color: btncolor },
                  }}
                />
              }
              label={
                <Typography sx={checkboxLabel}>
                  I understand and agree to the above statements
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={secondCheckBox}
                  onChange={handleSecondCheckbox}
                  sx={{
                    color: "rgba(125,125,125,0.5)",
                    "&.Mui-checked": { color: btncolor },
                  }}
                />
              }
              label={
                <Typography sx={checkboxLabel}>
                  I understand and agree to the{" "}
                  <Link
                    href={p2pTermsLink}
                    target="_blank"
                    rel="noopener"
                    sx={{ color: btncolor, textDecoration: "underline" }}
                  >
                    P2P terms of use
                  </Link>
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={thirdCheckBox}
                  onChange={handleThirdCheckbox}
                  sx={{
                    color: "rgba(125,125,125,0.5)",
                    "&.Mui-checked": { color: btncolor },
                  }}
                />
              }
              label={
                <Typography sx={{ ...checkboxLabel, opacity: 0.6 }}>
                  Do not show this to me again
                </Typography>
              }
            />
          </Box>
        </DialogContent>

        <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />
        <DialogActions sx={{ p: 2.5, backgroundColor: "var(--popupbody-color)" }}>
          <ContainedButton
            fullWidth
            variant="contained"
            size="large"
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            sx={{
              height: 48,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.95rem",
            }}
          >
            Confirm
          </ContainedButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const heroBanner = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
  maxWidth: 640,
};

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
  mb: 3,
};

const featureGrid = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
  gap: 2,
};

const featureCard = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.5,
  p: 2,
  backgroundColor: "var(--body-color-less2)",
  border: "1px solid rgba(125,125,125,0.12)",
  borderRadius: "12px",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 16px rgba(0,0,0,0.10)",
  },
};

const featureIconWrap = {
  width: 44,
  height: 44,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const featureTitle = {
  fontSize: "0.9rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.25,
  mb: 0.5,
};

const featureDesc = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.6,
  lineHeight: 1.6,
};

const howItWorksBtn = {
  borderRadius: "10px",
  px: 3,
  py: 1,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.85rem",
};

// Dialog
const dialogPaper = {
  borderRadius: "16px",
  minWidth: { xs: "85%", sm: 480 },
  boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
  overflow: "hidden",
  backgroundColor: "var(--popupbody-color)",
  color: "var(--text-color)",
};

const dialogTitleStyle = {
  textAlign: "center",
  fontWeight: 700,
  fontSize: "1.15rem",
  py: 2.25,
  color: "var(--text-color)",
  backgroundColor: "var(--popupheader-color)",
  position: "relative",
};

const dialogCloseBtn = {
  position: "absolute",
  right: 12,
  top: "50%",
  transform: "translateY(-50%)",
  color: "var(--text-color)",
  opacity: 0.6,
};

const dialogContentStyle = {
  pt: 3,
  pb: 1,
  px: { xs: 2.5, md: 3.5 },
  backgroundColor: "var(--popupbody-color)",
};

const noticeText = {
  fontSize: "0.85rem",
  color: "var(--text-color)",
  opacity: 0.8,
  lineHeight: 1.65,
  mb: 1.25,
  pl: 1.5,
  borderLeft: "3px solid rgba(125,125,125,0.2)",
};

const checkboxLabel = {
  fontSize: "0.85rem",
  color: "var(--text-color)",
  lineHeight: 1.5,
};

export default P2pDashboard;
